import {
  SimpleColumn,
  SimpleSortableTable,
} from "../../../../../components/ui/simple-sortable-table";
import { ImileCoerenciaRow, MotivoSuspeita } from "../types";

const moeda = (centavos: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(centavos / 100);

/**
 * Redação de cada motivo. O backend manda a chave, a tela só decide como mostrar
 * — assim a regra vive num lugar só (a CTE `suspeitos` do gestorCheckoutList).
 */
const MOTIVOS: Record<
  MotivoSuspeita,
  { label: string; className: string; title: string }
> = {
  sem_leituras: {
    label: "sem leituras",
    className: "bg-red-100 text-red-800",
    title:
      "Foi pago, mas não há nenhum waybill deste entregador no arquivo da iMile",
  },
  contagem_divergente: {
    label: "contagem divergente",
    className: "bg-red-100 text-red-800",
    title:
      "O número de pacotes do financeiro não bate com os waybills registrados",
  },
  valor_sem_pacotes: {
    label: "valor sem pacotes",
    className: "bg-red-100 text-red-800",
    title: "Há valor a pagar, mas o financeiro declara zero pacote",
  },
  ticket_fora_da_tarifa: {
    label: "ticket fora da tarifa",
    className: "bg-amber-100 text-amber-800",
    title:
      "Ticket acima do limite superior de Tukey — fora da tarifa praticada no dia",
  },
};

/**
 * Confronta o relatório financeiro da franquia com o relatório de entregas da
 * iMile, por entregador, mostrando SÓ os suspeitos.
 *
 * Serve para responder uma pergunta antes de aprovar a compra: existe alguém
 * sendo pago por entrega que a iMile não registrou?
 */
const columns: SimpleColumn<ImileCoerenciaRow>[] = [
  {
    key: "ops",
    label: "Base",
    render: (linha) => <span className="font-mono text-xs">{linha.ops}</span>,
  },
  {
    key: "nome",
    label: "Entregador",
    cellClassName: "font-medium",
    render: (linha) => (
      <div className="flex flex-col gap-1">
        <span>{linha.nome}</span>
        <div className="flex flex-wrap gap-1">
          {linha.motivos.map((motivo) => {
            const m = MOTIVOS[motivo];
            if (!m) return null;
            return (
              <span
                key={motivo}
                title={m.title}
                className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.className}`}
              >
                {m.label}
              </span>
            );
          })}
          {!linha.cpf && (
            <span
              title="Linha sem CPF: não pode ser paga, o checkout já a ignora"
              className="inline-flex w-fit items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
            >
              sem CPF
            </span>
          )}
        </div>
      </div>
    ),
  },
  {
    key: "pacotes_financeiro",
    label: "Pacotes (financeiro)",
    headClassName: "w-40 text-right",
    cellClassName: "text-right",
    render: (linha) => linha.pacotes_financeiro.toLocaleString("pt-BR"),
  },
  {
    key: "waybills",
    label: "Waybills (iMile)",
    headClassName: "w-36 text-right",
    cellClassName: "text-right",
    render: (linha) => linha.waybills.toLocaleString("pt-BR"),
  },
  {
    key: "diferenca",
    label: "Diferença",
    headClassName: "w-32 text-right",
    cellClassName: "text-right",
    render: (linha) =>
      linha.diferenca === 0 ? (
        <span className="text-muted-foreground">0</span>
      ) : (
        <span className="font-semibold text-red-600">
          {linha.diferenca > 0 ? `+${linha.diferenca}` : linha.diferenca}
        </span>
      ),
  },
  {
    key: "valor",
    label: "Valor",
    headClassName: "w-36 text-right",
    cellClassName: "text-right whitespace-nowrap",
    render: (linha) => moeda(linha.valor),
  },
  {
    key: "ticket",
    label: "Ticket / pacote",
    headClassName: "w-36 text-right",
    cellClassName: "text-right whitespace-nowrap",
    sortValue: (linha) => linha.ticket ?? -1,
    render: (linha) =>
      linha.ticket === null ? (
        <span className="text-muted-foreground">-</span>
      ) : (
        moeda(linha.ticket)
      ),
  },
];

interface CoerenciaTableProps {
  rows: ImileCoerenciaRow[];
  /** Total de entregadores conferidos no dia, o denominador dos suspeitos. */
  conferidos?: number;
}

export function CoerenciaTable({ rows, conferidos }: CoerenciaTableProps) {
  const valorSuspeito = rows.reduce((acc, l) => acc + l.valor, 0);
  const semLeituras = rows.filter((l) =>
    l.motivos.includes("sem_leituras")
  ).length;

  return (
    <div className="space-y-2">
      <div className="rounded-md border">
        <SimpleSortableTable
          columns={columns}
          rows={rows}
          rowKey={(linha, index) => `${linha.ops}-${linha.nome}-${index}`}
          rowClassName={(linha) =>
            linha.motivos.includes("sem_leituras") ||
            linha.motivos.includes("valor_sem_pacotes")
              ? "bg-red-50"
              : undefined
          }
        />
      </div>
      <div className="flex flex-wrap justify-end gap-6 px-1 text-sm">
        <span className="text-muted-foreground">
          {rows.length.toLocaleString("pt-BR")} suspeito
          {rows.length === 1 ? "" : "s"}
          {conferidos != null
            ? ` de ${conferidos.toLocaleString("pt-BR")} conferidos`
            : ""}
        </span>
        {semLeituras > 0 && (
          <span className="font-semibold text-red-700">
            {semLeituras.toLocaleString("pt-BR")} pago
            {semLeituras === 1 ? "" : "s"} sem nenhuma leitura
          </span>
        )}
        <span className="font-semibold">
          Valor sob suspeita: {moeda(valorSuspeito)}
        </span>
      </div>
    </div>
  );
}
